import { createTheme } from '@mui/material';

const brand = {
  main: '#1e4d72',
  light: '#3d6a94',
  dark: '#153a57',
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand.main,
      light: brand.light,
      dark: brand.dark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5b7fa3',
      light: '#7c9ab8',
      dark: '#3f5c78',
      contrastText: '#ffffff',
    },
    error: { main: '#dc2626' },
    warning: { main: '#d97706' },
    success: { main: '#16a34a' },
    divider: 'rgba(15, 23, 42, 0.09)',
    background: {
      default: '#f4f6f8',
      paper: '#ffffff',
      secondary: '#e8ecf1',
      modal: '#ffffff',
      messageBox: '#eceff2',
      received: brand.main,
      selected: '#5b9bd5',
      hover: 'rgba(30, 77, 114, 0.08)',
      internal: 'rgba(255, 236, 153, 0.55)',
      tabIndicator: brand.dark,
      textField: '#ffffff',
      icon: '#475569',
      toast: '#64748b',
      chatHeader: '#eef1f5',
      divider: 'rgba(15, 23, 42, 0.09)',
      card: '#ffffff',
    },
    quickAccess: {
      tabInactiveBg: 'rgba(255, 255, 255, 0.12)',
      tabInactiveText: 'rgba(255, 255, 255, 0.72)',
      tabInactiveBorder: 'rgba(255, 255, 255, 0.2)',
      tabInactiveHoverBg: 'rgba(255, 255, 255, 0.2)',
      tabActiveBg: 'rgba(255, 255, 255, 0.22)',
      tabActiveText: 'rgba(255, 255, 255, 0.98)',
      tabActiveBorder: 'rgba(255, 255, 255, 0.32)',
      tabActiveAccent: 'rgba(126, 200, 245, 0.7)',
      tabIcon: 'rgba(255, 255, 255, 0.85)',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
      caption: '#64748b',
      disabled: '#94a3b8',
      textField: '#0f172a',
      button: '#0f172a',
      buttonCaption: '#ffffff',
      tabSelected: '#0f172a',
      tabUnselected: '#64748b',
    },
    border: {
      primary: 'rgba(15, 23, 42, 0.1)',
      secondary: '#cbd5e1',
    },
    button: {
      primary: brand.main,
      secondary: '#5b7fa3',
      disabled: '#94a3b8',
    },
    switch: {
      on: brand.main,
      off: '#94a3b8',
      track: '#cbd5e1',
      trackChecked: '#93b8d3',
    },
    noticeBoard: {
      listSelectedBg: 'rgba(75, 134, 180, 0.18)',
      listSelectedBgHover: 'rgba(75, 134, 180, 0.28)',
      listSelectedBorder: '#4b86b4',
    },
    action: {
      hover: 'rgba(30, 77, 114, 0.06)',
      selected: 'rgba(30, 77, 114, 0.1)',
      disabled: 'rgba(15, 23, 42, 0.06)',
      disabledBackground: 'rgba(15, 23, 42, 0.06)',
    },
  },
  typography: {
    fontFamily: '"DM Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
    caption: { color: 'inherit' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { letterSpacing: '0.01em' },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 18px',
          minHeight: 40,
        },
        sizeSmall: {
          padding: '6px 14px',
          minHeight: 34,
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '10px 22px',
          minHeight: 46,
          fontSize: '1rem',
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }),
        containedSecondary: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
        }),
        outlined: ({ theme }) => ({
          borderWidth: '1.5px',
          borderColor: theme.palette.border.primary,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: 'rgba(30, 77, 114, 0.06)',
          },
        }),
        text: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        textPrimary: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderRadius: 10,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.secondary,
          '& .left-sidebar': {
            backgroundColor: 'var(--sidebar-shell-bg)',
            '& .MuiSvgIcon-root': {
              color: '#fff',
            },
            '& .MuiTypography-root.MuiTypography-caption': {
              color: '#fff',
            },
          },
          '& .header': {
            backgroundColor: 'var(--sidebar-shell-bg)',
            color: 'var(--white-900)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          },
          '& .header .MuiIconButton-root': {
            color: 'rgba(255,255,255,0.92)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.12)',
            },
          },
          '& .quick-access-bar .MuiIconButton-root': {
            color: 'inherit',
          },
          '& .quick-access-bar .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.background.icon,
          },
          /*
           * Ícone DENTRO de botão segue a cor do botão.
           *
           * A regra acima pinta todo ícone da tela com uma cor só, e por isso
           * ícone nenhum herda a cor de onde está. Num botão `contained` isso
           * dá ícone cinza-ardósia (#475569) sobre o azul do botão: medido em
           * **1,17:1**, ao lado de um texto branco em 8,6:1 -- o desenho
           * simplesmente some, que é o relato do Daniel (ação 27, item 1)
           * sobre o botão "Novo Filtro".
           *
           * `inherit` resolve os dois temas e as três variantes de uma vez:
           * seja qual for a cor do texto do botão, o ícone passa a ser a
           * mesma. A barra de acesso rápido, logo acima, já tinha precisado
           * desta mesma exceção -- sinal de que a regra geral é larga demais.
           */
          '& .MuiButton-root .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '& .profileSection': {
            border: `1px solid ${theme.palette.border.primary}`,
            width: '88vw',
            padding: '20px',
            marginTop: '20px',
            borderRadius: 12,
          },
          '& .separator': {
            width: '88vw',
            height: '1px',
            background: theme.palette.border.primary,
            marginTop: '20px',
          },
          '& .tabContent': {
            border: `1px solid ${theme.palette.border.primary}`,
            width: '88vw',
            padding: '20px',
            marginTop: '20px',
            marginLeft: '20px',
            borderRadius: 12,
          },
          '.MuiDataGrid-columnHeaderTitle': {
            color: 'var(--table-header-light)',
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.border.secondary,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1.5px',
          },
        }),
        input: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          color: theme.palette.text.primary,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '& .MuiInputBase-input': {
            backgroundColor: theme.palette.background.textField,
          },
        }),
        iconOutlined: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&.Mui-focused': { color: theme.palette.primary.main },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .Mui-focused fieldset': {
            borderColor: `${theme.palette.primary.main} !important`,
          },
          '& .MuiInputBase-root': {
            backgroundColor: theme.palette.background.textField,
            borderRadius: 10,
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          borderRadius: 10,
          '& .MuiFilledInput-notchedOutline': {
            borderWidth: '2px',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper || theme.palette.background.default,
          color: theme.palette.text.primary,
          borderRadius: 12,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 14,
          border: `1px solid ${theme.palette.border.primary}`,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          border: `1px solid ${theme.palette.border.primary}`,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            opacity: 1,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
        },
      },
    },
  },
});

export default lightTheme;
